import { useEffect, useRef, useState } from "react";
import { TenantForm } from "./useRegisterTenantButton";
import { useTranslation } from "react-i18next";
import { getCompatibleState } from "./CompatibleCountry";
import { getAuthItems } from "../auth/AuthItems";
import { searchTenantsForMailAddress } from "./AdminListApi";
import { callApi } from "../common/api/Api";

type Props = {
  formData: TenantForm;
  id: string;
  onConfirm: (form: TenantForm) => void;
  open: boolean;
  isEdit: boolean;
};

export const useInputCustomerTenantDialog = (props: Props) => {
  const { t } = useTranslation();
  const { formData, onConfirm, open } = props;
  const companyNameRef = useRef<HTMLInputElement>();
  const inputCountryRef = useRef<HTMLInputElement>();
  const inputStateRef = useRef<HTMLInputElement>();
  const [currentCountry, setCurrentCountry] = useState<string>("");
  const [stateList, setSetStateList] = useState<{ label: string; value: string }[]>([{ label: "", value: "" }]);
  const inputGivenNameRef = useRef<HTMLInputElement>();
  const inputFamilyNameRef = useRef<HTMLInputElement>();
  const inputEmailAddressRef = useRef<HTMLInputElement>();
  const [useCategory, setUseCategory] = useState<string>("");
  const [contractType, setContractType] = useState<string>("");
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [duplicatedDialogOpen, setDuplicatedDialogOpen] = useState(false);
  const [duplicatedAcceptButtonMessage, setDuplicatedAcceptButtonMessage] = useState("");
  const [duplicatedMessage, setDuplicatedMessage] = useState("");

  useEffect(() => {
    setUseCategory(formData.useCategory);
    setContractType(formData.contractType);
    setCurrentCountry(formData.country);
  }, [open]);

  useEffect(() => {
    const stateList: { label: string; value: string }[] = getCompatibleState(currentCountry, "");
    setSetStateList(stateList);
  }, [currentCountry]);

  const showCoufirm = () => {
    const eOfCompanyName = companyNameRef.current;
    const eOfCountryName = inputCountryRef.current;
    const eOfStateName = inputStateRef.current;
    const eOfGivenNameName = inputGivenNameRef.current;
    const eOfFamilyName = inputFamilyNameRef.current;
    const eOfEmailAddress = inputEmailAddressRef.current;

    if (
      !eOfCompanyName?.value ||
      !eOfCountryName?.value ||
      !eOfGivenNameName?.value ||
      !eOfFamilyName?.value ||
      !eOfEmailAddress?.value
    ) {
      setErrorMessage(t("admin.deficienciesCustomerTenantForm"));
      setErrorDialogOpen(true);
      return;
    }

    const form: TenantForm = {
      companyName: eOfCompanyName.value,
      useCategory,
      country: eOfCountryName.value,
      givenName: eOfGivenNameName.value,
      familyName: eOfFamilyName.value,
      emailAddress: eOfEmailAddress.value,
      contractType,
    };

    // RUS(北米極)の場合のみ州(state)の設定が必要
    if (getAuthItems().loginPole === "RUS") {
      if (!eOfStateName?.value) {
        setErrorMessage(t("admin.deficienciesCustomerTenantForm"));
        setErrorDialogOpen(true);
        return;
      }
      form.state = eOfStateName.value;
    }

    onConfirm(form);
  };

  const isDuplicatedMailAddress = async (mailAddress: string, isEdit: boolean) => {
    const result = await searchTenantsForMailAddress(mailAddress);
    if (result.error) {
      throw Error("searchTenantsForMailAddress is error.");
    }
    if (!result.data || !result.data.results) {
      throw Error("searchTenantsForMailAddress is error.data or results is undefined.");
    }

    // 新規登録時
    if (!isEdit) {
      // あるかどうかを返す
      return result.data?.results.length > 0;
    }

    // 更新時
    // 0件ならば、重複無し
    if (result.data?.results.length === 0) return false;
    // メールアドレスが同じ かつ IDが異なる場合は重複している
    return result.data.results.some((tenant) => {
      return tenant.contractors[0].mailAddress === mailAddress && tenant.id.toString() !== props.id;
    });
  };

  const onConfirmClick = async () => {
    const mailAddress = inputEmailAddressRef.current?.value ?? "";
    // メールアドレスが入力されているか、を確認する
    if (!mailAddress) {
      setErrorMessage(t("admin.deficienciesCustomerTenantForm"));
      setErrorDialogOpen(true);
      return;
    }
    await callApi(async () => {
      try {
        const isDuplicated = await isDuplicatedMailAddress(mailAddress, props.isEdit);
        // API呼び出しでエラーを出していたら、以降を実施しない
        if (errorDialogOpen) return;

        // すでに登録されているテナントの中に同じメールアドレスがあるか、を確認する
        if (isDuplicated) {
          if (props.isEdit) {
            setDuplicatedAcceptButtonMessage(t("common.updateButton"));
            setDuplicatedMessage(t("admin.duplicatedMailAddressToUpdaterMessage"));
          } else {
            setDuplicatedAcceptButtonMessage(t("common.registerButton"));
            setDuplicatedMessage(t("admin.duplicatedMailAddressToRegisterMessage"));
          }
          setDuplicatedDialogOpen(true);
        } else {
          showCoufirm();
        }
      } catch (e) {
        setErrorMessage("メールアドレス重複チェックのAPI呼び出しで失敗した");
        setErrorDialogOpen(true);
        return;
      }
    });
  };

  const onAcceptError = () => {
    setErrorDialogOpen(false);
  };

  const onAcceptDuplicated = () => {
    showCoufirm();
    setDuplicatedDialogOpen(false);
  };

  const onCloseDuplicated = () => {
    setDuplicatedDialogOpen(false);
  };

  return {
    companyNameRef,
    inputCountryRef,
    inputStateRef,
    currentCountry,
    setCurrentCountry,
    stateList,
    inputGivenNameRef,
    inputFamilyNameRef,
    inputEmailAddressRef,
    useCategory,
    setUseCategory,
    contractType,
    setContractType,
    onConfirmClick,
    errorDialogOpen,
    setErrorDialogOpen,
    errorMessage,
    duplicatedDialogOpen,
    duplicatedMessage,
    duplicatedAcceptButtonMessage,
    onAcceptError,
    onAcceptDuplicated,
    onCloseDuplicated,
  };
};
